import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, LanguageMenu, ThemeMenu } from '@easydent/shared-ui';
import { login } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

const LANGUAGES = [{ code: 'ro', label: 'Română' }, { code: 'en', label: 'English' }];

const LoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const s = await login(email, password);
      setSession(s.accessToken, s.name);
      navigate('/', { replace: true });
    } catch {
      // The server answers identically for every failure so accounts can't be enumerated;
      // the UI says the same thing.
      setError(t('login.failed', 'Invalid email or password.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-xl font-bold text-brand-ink-strong">easyDent</span>
          <LanguageMenu value={i18n.language} languages={LANGUAGES} onChange={(c) => i18n.changeLanguage(c)} />
          <ThemeMenu labels={{ label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'), dark: t('theme.dark', 'Dark'), system: t('theme.system', 'System') }} />
        </div>
        <form onSubmit={submit} className="rounded-xl border border-line bg-surface p-5 shadow-sm">
          <h1 className="mb-1 text-lg font-semibold text-body">{t('login.title', 'Imaging portal')}</h1>
          <p className="mb-4 text-sm text-muted">{t('login.subtitle', 'Referrals from the clinics you work with.')}</p>

          {error && <Alert variant="error" className="mb-3">{error}</Alert>}

          <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('login.email', 'Email')}</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            className="mb-3 w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <label className="mb-1 block text-xs uppercase tracking-wide text-subtle-ink">{t('login.password', 'Password')}</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="mb-4 w-full rounded-lg border border-line-strong px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
          <Button type="submit" loading={busy} className="w-full">{t('login.submit', 'Sign in')}</Button>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
